import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";

/**
 * The docs' theme choice (system / light / dark, default system), applied as the `dark` class and
 * `data-theme` on <html> — the switch the site's colour tokens read. Stored under the same key the
 * app uses (`theme`), so the `/docs` preview inside the app keeps the visitor's choice.
 */
export type Theme = "light" | "dark" | "system";

const KEY = "theme";

const stored = (): Theme => {
  try {
    const value = window.localStorage.getItem(KEY);

    return value === "light" || value === "dark" || value === "system" ? value : "system";
  } catch {
    return "system";
  }
};

const darkQuery = () => (typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia("(prefers-color-scheme: dark)") : null);

const ThemeContext = createContext<{ theme: Theme; setTheme: (theme: Theme) => void }>({ theme: "system", setTheme: () => undefined });

export const DocsThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setThemeState] = useState<Theme>(stored);
  const [systemDark, setSystemDark] = useState(() => darkQuery()?.matches ?? false);

  useEffect(() => {
    const query = darkQuery();
    if (!query?.addEventListener) return undefined;
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    query.addEventListener("change", onChange);

    return () => query.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const dark = theme === "dark" || (theme === "system" && systemDark);
    document.documentElement.classList.toggle("dark", dark);
    document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
  }, [theme, systemDark]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(KEY, next);
    } catch {
      // A browser that refuses storage keeps the choice for this visit only.
    }
  }, []);

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
};

export const useDocsTheme = () => useContext(ThemeContext);
